/**
 * Total Hip Arthroplasty (THA) Impairment Calculator
 * Ported from N8N workflow.
 * Based on AMA Guides 5th Edition, Tables 17-33 & 17-34, p546-548
 * Uses Harris Hip Score style with categorical selections
 */

// PORTED VERBATIM from QME - Tool src/lib/ratings/calculators/tha.ts (the trusted
// reference; its scripts/verify-legacy-calcs.mts pins it). Only these local types
// replace the '@/types/ratings' import. src/utils/tha.test.ts repeats its cases.
export type ThaSideInput = Record<string, string | null | undefined>;
export interface CalculatorInput {
  extractedData: { hips?: { tha_right?: ThaSideInput; tha_left?: ThaSideInput } } & Record<string, any>;
  side?: 'right' | 'left';
}
export interface CalculatorOutput {
  narrative: string;
  le?: number;
  wpi: number;
  breakdown?: Record<string, any>;
}

// ============= HELPER FUNCTIONS =============

const norm = (s: any) => (s || "").toString().replace(/\s+/g, " ").replace(/º|°/g, "°").trim().toLowerCase();

// AMA Table 17-34 maps (keys lowercased)
const MAPS = {
  Pain: { "none": 44, "slight": 40, "moderate occasional": 30, "moderate": 20, "marked": 10 },
  Limp: { "none": 11, "slight": 8, "moderate": 5, "severe": 0 },
  Device: {
    "none": 11, "cane for long walks": 7, "cane": 5, "one crutch": 3, "two canes": 2, "two crutches": 0
  },
  Distance: {
    "unlimited": 11, "six blocks": 8, "three blocks": 5, "indoors": 2, "in bed or chair": 0
  },
  Stairs: {
    "normal": 4, "using railing": 2, "cannot climb readily": 1, "unable to climb": 0
  },
  ShoesSocks: {
    "with ease": 4, "with difficulty": 2, "unable to do": 0
  },
  Sitting: {
    "any chair": 4, "high chair": 2, "unable to sit comfortably": 0
  },
  PublicTransport: {
    "able to use": 1, "unable to use": 0
  },
};

function mapFrom(map: Record<string, number>, value: any): number {
  if (!value) return 0;
  const k = norm(value);
  return map[k] ?? 0;
}

// Deformity: each favorable threshold earns 1 point
interface DeformityItem {
  label: string;
  value: string;
  points: number;
}

function deformityScore(sel: any): { pts: number; items: DeformityItem[] } {
  const items: DeformityItem[] = [];
  
  function add(label: string, value: any, goodPrefix: string) {
    const v = value || "";
    const ok = norm(v).startsWith(goodPrefix);
    const points = v ? (ok ? 1 : 0) : 0;
    const text = v || "[not specified]";
    items.push({ label, value: text, points });
  }

  add("Fixed adduction", sel.fixed_adduction, "<10");
  add("Fixed internal rotation", sel.fixed_internal_rotation, "<10");
  add("Fixed external rotation", sel.fixed_external_rotation, "<10");
  add("Flexion contracture", sel.flexion_contracture, "<15");
  add("Leg length discrepancy", sel.leg_length_discrepancy, "<1.5");

  const pts = items.reduce((a, b) => a + (b.points || 0), 0);
  return { pts, items };
}

// ROM: each favorable threshold earns 1 point
interface ROMItem {
  label: string;
  value: string;
  points: number;
}

function romScore(sel: any): { pts: number; items: ROMItem[] } {
  const items: ROMItem[] = [];
  
  function add(label: string, value: any, goodPrefix: string) {
    const v = value || "";
    const ok = norm(v).startsWith(goodPrefix);
    const points = v ? (ok ? 1 : 0) : 0;
    const text = v || "[not specified]";
    items.push({ label, value: text, points });
  }

  add("Flexion", sel.flexion, ">90");
  add("Abduction", sel.abduction, ">15");
  add("Adduction", sel.adduction, ">15");
  add("External rotation", sel.external_rotation, ">30");
  add("Internal rotation", sel.internal_rotation, ">15");

  const pts = items.reduce((a, b) => a + (b.points || 0), 0);
  return { pts, items };
}

// ============= MAIN THA CALCULATOR =============

export function calculateTHAImpairment(input: CalculatorInput): CalculatorOutput {
  const { extractedData, side = 'right' } = input;
  
  // Get THA data from extraction
  const src: any = side === 'left'
    ? (extractedData as any).hips?.tha_left
    : (extractedData as any).hips?.tha_right;
  
  if (!src) {
    return {
      narrative: `No ${side} total hip arthroplasty data found in the physical examination. Unable to calculate impairment.`,
      le: 0,
      wpi: 0,
      breakdown: {
        error: 'Missing THA data',
        side
      }
    };
  }
  
  // Compute section points
  const painDesc = src.pain || null;
  const painPoints = mapFrom(MAPS.Pain, painDesc);
  
  const gaitDesc = src.limp || null;
  const gaitPoints = mapFrom(MAPS.Limp, gaitDesc);
  
  const deviceDesc = src.supportive_device || null;
  const devicePoints = mapFrom(MAPS.Device, deviceDesc);
  
  const distanceDesc = src.distance_walked || null;
  const distancePoints = mapFrom(MAPS.Distance, distanceDesc);
  
  const stairsDesc = src.stairs_climbing || null;
  const stairsPoints = mapFrom(MAPS.Stairs, stairsDesc);
  
  const shoesDesc = src.shoes_socks || null;
  const shoesPoints = mapFrom(MAPS.ShoesSocks, shoesDesc);
  
  const sittingDesc = src.sitting || null;
  const sittingPoints = mapFrom(MAPS.Sitting, sittingDesc);
  
  const transportDesc = src.public_transportation || null;
  const transportPoints = mapFrom(MAPS.PublicTransport, transportDesc);
  
  // Deformity & ROM with itemized breakdown
  const d = deformityScore(src);
  const r = romScore(src);
  
  // Calculate totals
  const functionPoints = gaitPoints + devicePoints + distancePoints;
  const activitiesPoints = stairsPoints + shoesPoints + sittingPoints + transportPoints;
  const totalPoints = painPoints + functionPoints + activitiesPoints + d.pts + r.pts;
  
  // Determine result tier
  let resultDesc: string, lePercent: number, wpiPercent: number;
  if (totalPoints >= 85) { resultDesc = 'good'; lePercent = 37; wpiPercent = 15; }
  else if (totalPoints >= 50) { resultDesc = 'fair'; lePercent = 50; wpiPercent = 20; }
  else { resultDesc = 'poor'; lePercent = 75; wpiPercent = 30; }
  
  // ============= BUILD NARRATIVE (EXACT from N8N) =============
  
  const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);
  const functionMath = `${gaitPoints} (gait) + ${devicePoints} (device) + ${distancePoints} (distance) = ${functionPoints}`;
  const activitiesMath = `${stairsPoints} (stairs) + ${shoesPoints} (shoes/socks) + ${sittingPoints} (sitting) + ${transportPoints} (public transport) = ${activitiesPoints}`;
  const totalMath = `${painPoints} (pain) + ${functionPoints} (function) + ${activitiesPoints} (activities) + ${d.pts} (deformity) + ${r.pts} (ROM) = ${totalPoints}`;
  
  const line = (label: string, val: any, pts: number) => `- ${label}: ${val || "[not specified]"} → ${pts} points.`;
  const linesFromItems = (items: Array<{label: string; value: string; points: number}>) => 
    items.map(it => `- ${it.label}: ${it.value} → ${it.points} point${it.points === 1 ? "" : "s"}.`).join("\n");
  
  const narrative = 
    `<b><u>${capitalizedSide} Total Hip Arthroplasty</u></b>\n\n` +
    `I refer the parties to Table 17-34 on page 548. The applicant underwent a ${capitalizedSide} total hip arthroplasty. Five criteria are used in the rating for hip replacement: pain, function, activities, deformity, and range of motion.\n\n` +
    `Pain (1):\n` +
    `${line("Pain", painDesc, painPoints)}\n\n` +
    `Function (2):\n` +
    `${line("Limp", gaitDesc, gaitPoints)}\n` +
    `${line("Supportive device", deviceDesc, devicePoints)}\n` +
    `${line("Distance walked", distanceDesc, distancePoints)}\n` +
    `- Subtotal (function): ${functionMath} points.\n\n` +
    `Activities (3):\n` +
    `${line("Stairs climbing", stairsDesc, stairsPoints)}\n` +
    `${line("Putting on shoes and socks", shoesDesc, shoesPoints)}\n` +
    `${line("Sitting", sittingDesc, sittingPoints)}\n` +
    `${line("Public transportation", transportDesc, transportPoints)}\n` +
    `- Subtotal (activities): ${activitiesMath} points.\n\n` +
    `Deformity (4):\n` +
    `${linesFromItems(d.items)}\n` +
    `- Subtotal (deformity): ${d.pts} points.\n\n` +
    `Range of Motion (5):\n` +
    `${linesFromItems(r.items)}\n` +
    `- Subtotal (ROM): ${r.pts} points.\n\n` +
    `Grand Total:\n` +
    `- ${totalMath} points.\n\n` +
    `I now refer the parties to Table 17-33 on page 546. ${totalPoints} points corresponds to a "${resultDesc}" result. This is ${lePercent}% lower extremity impairment which converts to ${wpiPercent}% whole person impairment. This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  
  // ============= RETURN =============
  
  return {
    narrative,
    le: lePercent,
    wpi: wpiPercent,
    breakdown: {
      pain: painPoints,
      function: functionPoints,
      activities: activitiesPoints,
      deformity: d.pts,
      rom: r.pts,
      total: totalPoints,
      result: resultDesc,
    },
  };
}
